const MINUTE_MS = 60_000;
const STUNDE_MS = 60 * MINUTE_MS;
const TAG_MS = 24 * STUNDE_MS;

const RELATIV = new Intl.RelativeTimeFormat("de", { numeric: "auto" });
const DATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Relative deutsche Beschreibung eines Zeitpunkts ("vor 5 Minuten", "gestern").
 * Ab einer Woche wird auf ein festes Datum umgestellt — "vor 41 Tagen" liest sich
 * ab dort schlechter als ein Kalenderdatum.
 */
export function relativeZeit(iso: string, jetzt: Date = new Date()): string {
  const zeitpunkt = new Date(iso);
  const differenzMs = zeitpunkt.getTime() - jetzt.getTime();
  const absMs = Math.abs(differenzMs);

  if (absMs < MINUTE_MS) return "gerade eben";
  if (absMs < STUNDE_MS) return RELATIV.format(Math.round(differenzMs / MINUTE_MS), "minute");
  if (absMs < TAG_MS) return RELATIV.format(Math.round(differenzMs / STUNDE_MS), "hour");
  if (absMs < 7 * TAG_MS) return RELATIV.format(Math.round(differenzMs / TAG_MS), "day");
  return formatDatum(iso);
}

/**
 * Der heutige örtliche Kalendertag als ISO-Datum (YYYY-MM-DD) — für „bestellt am“ und die Stichtag-Vorgabe des
 * Rechnungsvorschlags. Örtlich und nicht `toISOString().slice(0, 10)`: Der UTC-Tag ist zwischen 0 und 1 bzw. 2 Uhr noch
 * der Vortag, der Server rechnet mit dem Tag in Berlin (`packages/db/src/tag.ts`). Steht hier und nicht in
 * `AuftragDetail.tsx`, weil auch `Rechnungsvorschlag.tsx` ihn braucht und von dort ein Importkreis entstünde. Die
 * EINE Stelle in web: auch der Festschreib- und Bezahlt-Dialog (`RechnungDetail.tsx`) und das Austrittsdatum eines
 * Ansprechpartners (`KundeAnsprechpartner.tsx`) nehmen ihn von hier.
 */
export function heuteAlsTag(jetzt: Date = new Date()): string {
  const mm = String(jetzt.getMonth() + 1).padStart(2, "0");
  const tt = String(jetzt.getDate()).padStart(2, "0");
  return `${String(jetzt.getFullYear())}-${mm}-${tt}`;
}

/** Reiner Kalendertag ohne Uhrzeit, wie ihn eine DATE-Spalte liefert ("Kunde seit"). */
const NUR_DATUM = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Festes Datum im deutschen Format, z. B. für "Kunde seit".
 *
 * Ein reiner Kalendertag trägt weder Uhrzeit noch Zone. new Date("2024-01-01") liest ihn
 * nach ECMA-262 als UTC-Mitternacht — westlich von Greenwich stünde damit der Vortag auf
 * dem Bildschirm. Deshalb wird er als örtlicher Kalendertag zusammengesetzt; echte
 * Zeitpunkte (ISO-8601 mit "Z", so wie die API sie liefert) laufen unverändert weiter.
 */
export function formatDatum(iso: string): string {
  const teile = NUR_DATUM.exec(iso);
  if (teile !== null) {
    return DATUM.format(
      new Date(Number(teile[1]), Number(teile[2]) - 1, Number(teile[3])));
  }
  return DATUM.format(new Date(iso));
}

const MONTH_FORMAT = new Intl.DateTimeFormat("de-DE", { month: "long" });

/** Der deutsche Name eines Monats 1–12 („März“) — aus `Intl`, keine eigene Liste (Final-Fixrunde Lieferanten, A-K3). */
export function monthName(month: number): string {
  return month >= 1 && month <= 12 ? MONTH_FORMAT.format(new Date(2000, month - 1, 1)) : String(month);
}

const ZEITPUNKT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});

/**
 * Ein Zeitpunkt MIT Uhrzeit, deutsch: „05.09.2026, 10:30" — für Angaben, bei denen die
 * Minute zur Aussage gehört. Der Dienste-Status ist der erste Fall: „Stand: heute" wäre
 * dort wertlos, weil ein Aktualisieren-Knopf daneben steht und man sehen können muss, ob
 * er gewirkt hat.
 *
 * Abgrenzung zu `formatDatum` (nur Tag, für Belegdaten) und `relativeZeit` („vor 5
 * Minuten", für die letzte Aktivität eines Kunden). Die Uhrzeit erscheint in der Zone des
 * Geräts — genau richtig für eine Messung, die gerade eben stattgefunden hat.
 */
export function formatZeitpunkt(iso: string): string {
  return ZEITPUNKT.format(new Date(iso));
}

/**
 * Der deutsche Anzeigename eines Länderkürzels — aus `Intl.DisplayNames`, nicht aus einer
 * eigenen Tabelle: Die zulässigen KÜRZEL liefert der Server (aus der Domänenkonstante,
 * gegen die er auch prüft), die NAMEN kennt die Plattform. Zwei Quellen, die beide nicht
 * im Web abgeschrieben stehen und deshalb nicht veralten können. Der Rückfall auf das
 * Kürzel deckt beides ab: eine Plattform ohne deutsche Namen und ein Kürzel, das `of` als
 * unwohlgeformt zurückweist.
 *
 * Steht HIER und nicht in einer Ansicht, weil zwei Masken dieselbe Beschriftung brauchen
 * (Unternehmens-Bereich der Verwaltung und Kunden-Stammdaten) — zwei Fassungen hießen
 * dasselbe Land an zwei Bildschirmen womöglich verschieden.
 */
const LAND_ANZEIGE = new Intl.DisplayNames(["de"], { type: "region" });

export function landName(code: string): string {
  try { return LAND_ANZEIGE.of(code) ?? code; } catch { return code; }
}

const BETRAG = new Intl.NumberFormat("de-DE",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Ein Betrag mit Währung, deutsch: „1.234,50 €". `EUR` erscheint als `€`; ein unbekannter
 * Code bleibt als Code stehen — eine erfundene Abkürzung wäre schlimmer als ein sperriges
 * „1.234,50 CHF".
 *
 * Steht HIER und nicht in den Ansichten: Dieselben sechs Zeilen standen wortgleich in
 * KundeAngebote, KundeAuftraege und KundeRechnungen. Drei Fassungen bedeuten drei
 * Gelegenheiten, dass derselbe Betrag an drei Bildschirmen verschieden aussieht — und
 * `format.ts` ist der eingerichtete Ort dafür (wie `formatDatum` und `formatKondition`).
 *
 * NIE `String(betrag)` in eine Zelle oder ein Feld: „200.55" auf einem deutschen Bildschirm
 * ist zweideutig, und einmal zurückgelesen wurden daraus 20055 (Fund W1 der Fix-Runde zu
 * Paket 3a).
 */
export function formatBetrag(betrag: number, currency: string): string {
  const zahl = BETRAG.format(betrag);
  return currency === "EUR" ? `${zahl} €` : `${zahl} ${currency}`;
}

const MENGE = new Intl.NumberFormat("de-DE",
  { minimumFractionDigits: 0, maximumFractionDigits: 3 });

/**
 * Eine MENGE deutsch — „3", „1,5", „0,125". Ohne erfundene Nachkommastellen (eine Menge 3
 * ist eine 3 und keine 3,00) und höchstens drei davon, wie die Domäne sie führt.
 *
 * Steht hier und nicht dreimal daneben: Dieselbe `Intl`-Einstellung stand wortgleich im
 * Positions-Editor, im Rechnungsdetail und in der Vorlagensicht (Befund Fixrunde 1 zu
 * Aufgabe 7). Drei Fassungen sind drei Gelegenheiten, dass dieselbe Menge an drei
 * Bildschirmen verschieden aussieht.
 */
export function formatMenge(menge: number): string {
  return MENGE.format(menge);
}

/** Byte-Zahl menschenlesbar: „2.0 KB", „1.4 MB". Basis 1024 (wie die Grenzangabe „50 MB"
 *  der Upload-Route). Unter 1 KB in Bytes, damit eine kleine Datei nicht als „0.0 KB" steht.
 *  Steht hier, weil der Reiter Dateien und die Kachel der Kundenübersicht dieselbe Größe zeigen. */
export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const einheiten = ["KB", "MB", "GB", "TB"];
  let wert = bytes / 1024;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${wert.toFixed(1)} ${einheiten[i]}`;
}

/**
 * Eine DEZIMAL-Zeichenkette der API („1234.56") als deutsche ZAHL, ohne Währung — und
 * dasselbe für eine Zahl, die schon eine ist (die Entwurfsfelder des Positions-Editors
 * füllen ihre Eingaben damit).
 *
 * `Number()` ist hier unbedenklich und ausdrücklich keine zweite Rechnung: Der Wert ist auf
 * dem Server bereits auf Cent gerundet und wird nur ANGEZEIGT. Was sich nicht als Zahl lesen
 * lässt, bleibt UNVERÄNDERT stehen — lieber ein sichtbar sonderbarer Wert als ein „NaN", das
 * aussieht wie ein Fehler der Maske.
 */
export function formatDezimal(wert: string | number): string {
  const zahl = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(zahl) ? BETRAG.format(zahl) : String(wert);
}

/** Dieselbe Dezimal-Zeichenkette MIT Währung („1.234,56 €") — die Form, in der die Summen
 *  einer Positionsantwort auf dem Bildschirm stehen. */
export function formatDezimalBetrag(wert: string, currency: string): string {
  const zahl = Number(wert);
  return Number.isFinite(zahl) ? formatBetrag(zahl, currency) : wert;
}

/**
 * Die Werte einer geltenden Zahlungskondition — absichtlich als Gestalt, nicht als der
 * Typ `AufgeloesteKondition` aus dem Klienten: Dieselbe Formatierung gilt für einen
 * gepflegten Stammsatz in der Verwaltungsliste (Aufgabe 11), und der trägt kein `source`.
 */
export type Konditionswerte = {
  netDays: number;
  discountPercent: number | null;
  discountDays: number | null;
};

const PROZENT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

/**
 * Die geltende Zahlungskondition in einem Satz: „30 Tage netto", „14 Tage, 2 % Skonto bis
 * Tag 14", „— keine Kondition vereinbart".
 *
 * Die EINE Formatierungsstelle des Systems. Sie steht hier und nicht in der Ansicht, weil
 * dieselbe Zeichenkette an mindestens zwei Stellen gebraucht wird — an der Kundenbeziehung
 * im Stammdaten-Tab und in der Verwaltungsliste der Standards. Zwei Fassungen liefen
 * auseinander, und dann hiesse dieselbe Kondition an zwei Bildschirmen Verschiedenes.
 *
 * `null` heisst „keine Kondition vereinbart" — genau das, was `resolved` der Route
 * `/api/relations/:id/terms` mit `null` sagt (Entscheidung AK: kein source „NONE").
 *
 * Skonto wird nur genannt, wenn BEIDE Angaben da sind: Ein Satz ohne Frist (oder umgekehrt)
 * ist keine anwendbare Kondition — der Server lässt ihn gar nicht erst zu (`validateTerms`),
 * und ein halber Skontosatz auf dem Bildschirm sähe aus wie eine Zusage.
 */
export function formatKondition(werte: Konditionswerte | null): string {
  if (werte === null) return "— keine Kondition vereinbart";
  const tage = `${werte.netDays} ${werte.netDays === 1 ? "Tag" : "Tage"}`;
  if (werte.discountPercent === null || werte.discountDays === null) {
    return `${tage} netto`;
  }
  return `${tage}, ${PROZENT.format(werte.discountPercent)} % Skonto `
    + `bis Tag ${werte.discountDays}`;
}

/**
 * Die `tel:`-Adresse zu einer frei getippten Nummer (RFC 3966): ein „+“ und Ziffern; Leerzeichen, Klammern, Bindestriche,
 * Schrägstriche und Freitext davor („Tel. +49 …“) fallen weg. Die Landesvorwahl zählt als „+49“ wie als „0049“, eine „(0)“
 * direkt dahinter wählt man nicht mit. Ohne eine Ziffer gibt es keine Adresse (`null`): Dann steht der Text da und kein
 * Verweis, der ins Leere wählt.
 */
export function telHref(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  const number = text.slice(Math.max(text.search(/[+\d]/), 0));
  const country = /^(?:\+|00)\s*(\d{1,3})\s*(?:\(\s*0\s*\))?/.exec(number);
  const digits = (country === null ? number : country[1] + number.slice(country[0].length)).replace(/\D/g, "");
  return digits === "" ? null : `tel:${country === null ? "" : "+"}${digits}`;
}

/**
 * Die `mailto:`-Adresse zu einer Mailadresse. Sie ist Freitext aus den Stammdaten; enthielte sie `?`, `&` oder `#`, hängte der Verweis
 * Zusatzköpfe an (`?bcc=…`), mit `,` oder `;` weitere Empfänger. Solche Werte und Werte ohne „@“ oder mit Leerraum bekommen keine Adresse
 * (`null`), der Text steht dann ohne Verweis da.
 */
export function mailtoHref(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  if (!text.includes("@") || /[?&#,;\s]/.test(text)) return null;
  try {
    return `mailto:${encodeURI(text)}`;
  } catch {
    return null;   // ein einzelnes Surrogat (URIError) wird Text, statt die Kopfkachel im Zeichnen abstürzen zu lassen
  }
}

const LIST = new Intl.ListFormat("de", { type: "conjunction" });
/** Eine Aufzählung mit „und“: „BS und PA“, „BS, ME und PA“ (Akte eines fremden Unternehmens, Ansagen der Übersicht). */
export const listText = (parts: readonly string[]): string => LIST.format(parts);

/**
 * Die zweite Zeile einer ARTIKELZEILE: Preis, Einheit und Kategorie.
 *
 * Entscheid Dirk 23.09.2026 (Screenshot Verwaltung → Artikelstamm): „Zusätzlich sollte
 * immer der Preis und die Kategorie zu sehen sein. Dafür kann der Beschreibungstext bei
 * Bedarf kürzer werden oder entfallen." Der Anriss ist daraufhin aus beiden Artikellisten
 * entfallen; die vollständige Beschreibung steht im Lese-Detail.
 *
 * Der Satz steht HIER und nicht in einer der beiden Ansichten, weil ihn zwei brauchen: der
 * Artikel-BEREICH (`/artikel`) und der Artikelstamm der VERWALTUNG. Zwei Abschriften liefen
 * beim ersten Umformulieren auseinander, und dann sagte derselbe Artikel an zwei Stellen
 * etwas anderes über sich.
 *
 * Eine fehlende Kategorie wird AUSDRÜCKLICH genannt („ohne Kategorie") und nicht
 * weggelassen: Eine Zeile, die nach dem Preis einfach endet, sieht wie ein Anzeigefehler
 * aus — und „hat keine Kategorie" ist eine Auskunft, die man in der Pflegesicht braucht.
 *
 * Primitive Parameter und nicht der `Artikel`-Satz: So bleibt format.ts frei von
 * Vertragstypen, und der Helfer ist mit drei Zahlen prüfbar.
 */
export function artikelMeta(
  netUnitPrice: number, unit: string, category: string | null,
): string {
  const kategorie = category === null || category.trim() === ""
    ? "ohne Kategorie" : category;
  return `${formatBetrag(netUnitPrice, "EUR")} netto je ${unit} · ${kategorie}`;
}

/**
 * Die Marke der Abrechnungsquelle eines Artikels (Spec „Abrechnungsquellen") — für die
 * Artikelzeile (`marken`) und das Detail. `null`, wenn der Artikel keine Quelle hat: Dann
 * steht dort NICHTS (Spec: „nur wenn gesetzt").
 */
export function quellenMarke(
  quelle: "MONITOR" | "AIRCALL" | null | undefined,
): string | null {
  if (quelle === "MONITOR") return "Daten aus Monitor";
  if (quelle === "AIRCALL") return "Daten aus Aircall";
  return null;
}

/**
 * Der ERSTE ABSATZ eines Markdown-Texts — der Kurztext der Artikelkachel (Entscheid 724, Plan P12): alles bis zur ersten
 * Leerzeile (auch einer nur aus Leerraum, auch mit CRLF aus dem Altbestand), getrimmt. Einfache Zeilenwechsel bleiben, sie gehören zum Absatz. Leer oder
 * fehlend → `null`; führende Leerzeilen zählen nicht als Absatz.
 */
export function firstParagraph(markdown: string | null): string | null {
  if (markdown === null) return null;
  const erster = markdown.trim().split(/\r?\n[ \t\r]*\n/)[0]?.trim() ?? "";
  return erster === "" ? null : erster;
}
